<template>
  <b-card-code
    title="Ajouter une localité logistique"
    aria-label="Formulaire d’ajout de localité logistique"
    role="form"
  >
    <b-card-text class="mt-1">
      <span id="locality-form-desc">Veuillez saisir les informations territoriales de la localité. Les champs marqués d’un astérisque (*) sont obligatoires. Utilisez une désignation officielle et unique.</span>
      <span class="sr-only">Formulaire accessible pour l’ajout d’une localité logistique. Tous les champs sont requis pour la gestion professionnelle du territoire.</span>
    </b-card-text>
    <validation-observer ref="simpleRules">
      <b-form aria-labelledby="locality-form-desc">
        <b-row>
          <b-col md="12">
            <b-form-group
              label-for="locality-name"
              description="Nom officiel de la localité logistique."
            >
              <template #label>
                Nom de la localité <span class="text-danger">*</span>
              </template>
              <validation-provider
                #default="{ errors }"
                name="Nom de la localité"
                rules="required"
              >
                <b-form-input
                  id="locality-name"
                  v-model="locality.name"
                  :state="errors.length > 0 ? false : null"
                  placeholder="Nom officiel de la localité"
                  aria-required="true"
                  aria-describedby="locality-name-help"
                />
                <small
                  id="locality-name-help"
                  class="form-text text-muted"
                >Saisissez le nom officiel utilisé dans la logistique.</small>
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </b-col>
          <b-col md="12">
            <b-form-group
              label-for="locality-delegation"
              description="Sélectionnez la délégation de rattachement."
            >
              <template #label>
                Délégation <span class="text-danger">*</span>
              </template>
              <validation-provider
                #default="{ errors }"
                name="Délégation"
                rules="required"
              >
                <v-select
                  id="locality-delegation"
                  v-model="locality.delegation"
                  :clearable="false"
                  placeholder="Choisir une délégation"
                  label="name"
                  :options="delegations"
                  aria-required="true"
                  aria-describedby="locality-delegation-help"
                />
                <small
                  id="locality-delegation-help"
                  class="form-text text-muted"
                >Sélectionnez la délégation à laquelle appartient la localité.</small>
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </b-col>
          <b-col md="12">
            <b-form-group
              label-for="locality-zip"
              description="Code postal officiel de la localité."
            >
              <template #label>
                Code postal <span class="text-danger">*</span>
              </template>
              <validation-provider
                #default="{ errors }"
                name="Code Postal"
                rules="required"
              >
                <b-form-input
                  id="locality-zip"
                  v-model="locality.zip_code"
                  :state="errors.length > 0 ? false : null"
                  placeholder="Code postal"
                  type="number"
                  maxlength="4"
                  aria-required="true"
                  aria-describedby="locality-zip-help"
                />
                <small
                  id="locality-zip-help"
                  class="form-text text-muted"
                >Saisissez le code postal officiel de la localité.</small>
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </b-col>
          <b-col cols="12">
            <b-button
              variant="primary"
              type="submit"
              class="mb-1 mb-sm-0 mr-0 mr-sm-1"
              :class="{ 'disabled-cursor': isLoading || !isLocalityValid }"
              :disabled="isLoading || !isLocalityValid"
              aria-label="Ajouter la localité logistique"
              @click.prevent="createLocality"
            >
              Ajouter
            </b-button>
          </b-col>
        </b-row>
      </b-form>
    </validation-observer>
  </b-card-code>
</template>

<script>
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import BCardCode from '@core/components/b-card-code'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import vSelect from 'vue-select'

import {
  BFormInput,
  BFormGroup,
  BForm,
  BRow,
  BCol,
  BButton,
  BCardText,
} from 'bootstrap-vue'
import { required } from '@validations'
import axios from 'axios'

export default {
  components: {
    BCardCode,
    ValidationProvider,
    ValidationObserver,
    BCardText,
    BFormInput,
    BFormGroup,
    BForm,
    BRow,
    BCol,
    BButton,
    vSelect,
    // eslint-disable-next-line vue/no-unused-components
    ToastificationContent,
  },
  data() {
    return {
      locality: {
        name: '',
        delegation: '',
        zip_code: '',
      },
      delegations: [],
      required,
      isLoading: false,
    }
  },
  computed: {
    isLocalityValid() {
      return (
        this.locality.name !== ''
        && this.locality.delegation.id !== ''
        && this.locality.zip_code !== ''
      )
    },
  },
  created() {
    this.getDelegations()
  },
  methods: {
    async getDelegations() {
      try {
        this.isLoading = true
        const { data } = await axios.get('/api/delegations/')
        this.delegations = data
        this.isLoading = false
      } catch (error) {
        this.showToast('danger', 'top-center', 'Network error: ')
      }
    },
    async createLocality() {
      const success = await this.$refs.simpleRules.validate()
      if (success) {
        try {
          this.isLoading = true
          await axios.post('/api/localities/create/', {
            name: this.locality.name,
            zip_code: this.locality.zip_code,
            delegation: this.locality.delegation.id,
          })
          this.isLoading = false
          setTimeout(() => {
            this.showToast('success', 'top-center', 'localité ajouté avec succés')
          }, 1000)
          this.$router.push('/localities/')
        } catch (error) {
          setTimeout(() => {
            this.showToast('danger', 'top-center', error.toString())
          }, 1000)
        }
      } else {
        setTimeout(() => {
          this.showToast('danger', 'top-center', 'champ obligatoire ! vérifier votre donnée !')
        }, 1000)
      }
    },
    showToast(variant, position, text) {
      this.$toast(
        {
          component: ToastificationContent,
          props: {
            title: 'Notification',
            icon: 'InfoIcon',
            text,
            variant,
          },
        },
        {
          position,
        },
      )
    },
  },
}
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>
